/**
 * i18next catalog wiring. `en` is the source of truth; `fr` is gate-kept to
 * mirror it (see `scripts/i18n-check.sh`). The dev-only `en-XX` pseudo-locale is
 * generated from `en` at runtime so it never needs hand-maintained files.
 *
 * Only `en` is bundled eagerly. It is the `fallbackLng`, so it has to be
 * resolvable synchronously for every `t()` call regardless of the active
 * language. Other languages load on demand through {@link loadLanguageCatalog}
 * — shipping every catalog to every user put ~600 KB of French on the native
 * boot path, which is budgeted by `scripts/check-native-bundle.sh`.
 */

import enAdmin from '@/locales/en/admin.json';
import enAds from '@/locales/en/ads.json';
import enAuth from '@/locales/en/auth.json';
import enAutomations from '@/locales/en/automations.json';
import enCampaigns from '@/locales/en/campaigns.json';
import enClipStudio from '@/locales/en/clip-studio.json';
import enCommon from '@/locales/en/common.json';
import enComposer from '@/locales/en/composer.json';
import enConstituentProfile from '@/locales/en/constituent-profile.json';
import enCorrespondence from '@/locales/en/correspondence.json';
import enCreative from '@/locales/en/creative.json';
import enCrm from '@/locales/en/crm.json';
import enDashboard from '@/locales/en/dashboard.json';
import enDiscover from '@/locales/en/discover.json';
import enErrors from '@/locales/en/errors.json';
import enExplorer from '@/locales/en/explorer.json';
import enKnowledge from '@/locales/en/knowledge.json';
import enNotifications from '@/locales/en/notifications.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enOrganisations from '@/locales/en/organisations.json';
import enPulseAi from '@/locales/en/pulse-ai.json';
import enQuickDrafts from '@/locales/en/quick-drafts.json';
import enReporting from '@/locales/en/reporting.json';
import enSettings from '@/locales/en/settings.json';
import enShareViewer from '@/locales/en/share-viewer.json';
import enTasks from '@/locales/en/tasks.json';
import enWebsites from '@/locales/en/websites.json';

import { pseudoLocalizeCatalog } from './pseudo-locale';

import type { Resource, ResourceLanguage } from 'i18next';

export const FALLBACK_LANGUAGE = 'en' as const;
export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export const I18N_NAMESPACES = [
  'admin',
  'ads',
  'auth',
  'automations',
  'campaigns',
  'clip-studio',
  'common',
  'composer',
  'constituent-profile',
  'correspondence',
  'creative',
  'crm',
  'dashboard',
  'discover',
  'errors',
  'explorer',
  'knowledge',
  'notifications',
  'onboarding',
  'organisations',
  'pulse-ai',
  'quick-drafts',
  'reporting',
  'settings',
  'share-viewer',
  'tasks',
  'websites',
] as const;
export const DEFAULT_NAMESPACE = 'common' as const;

/** Dev-only locale that accents `en` strings to surface missed `t()` calls + overflow. */
export const PSEUDO_LOCALE = 'en-XX' as const;

export const BASE_RESOURCES: Resource = {
  en: {
    admin: enAdmin,
    ads: enAds,
    auth: enAuth,
    automations: enAutomations,
    campaigns: enCampaigns,
    'clip-studio': enClipStudio,
    common: enCommon,
    composer: enComposer,
    'constituent-profile': enConstituentProfile,
    correspondence: enCorrespondence,
    creative: enCreative,
    crm: enCrm,
    dashboard: enDashboard,
    discover: enDiscover,
    errors: enErrors,
    explorer: enExplorer,
    knowledge: enKnowledge,
    notifications: enNotifications,
    onboarding: enOnboarding,
    organisations: enOrganisations,
    'pulse-ai': enPulseAi,
    'quick-drafts': enQuickDrafts,
    reporting: enReporting,
    settings: enSettings,
    'share-viewer': enShareViewer,
    tasks: enTasks,
    websites: enWebsites,
  },
};

/**
 * Base catalogs plus the dev-only pseudo-locale. Callers pick this over
 * {@link BASE_RESOURCES} only in dev, so the pseudo strings never reach a real
 * user (the dev/prod choice lives at the i18next bootstrap, not here).
 */
export function buildDevResources(): Resource {
  return {
    ...BASE_RESOURCES,
    [PSEUDO_LOCALE]: {
      admin: pseudoLocalizeCatalog(enAdmin),
      ads: pseudoLocalizeCatalog(enAds),
      auth: pseudoLocalizeCatalog(enAuth),
      automations: pseudoLocalizeCatalog(enAutomations),
      campaigns: pseudoLocalizeCatalog(enCampaigns),
      'clip-studio': pseudoLocalizeCatalog(enClipStudio),
      common: pseudoLocalizeCatalog(enCommon),
      composer: pseudoLocalizeCatalog(enComposer),
      'constituent-profile': pseudoLocalizeCatalog(enConstituentProfile),
      correspondence: pseudoLocalizeCatalog(enCorrespondence),
      creative: pseudoLocalizeCatalog(enCreative),
      crm: pseudoLocalizeCatalog(enCrm),
      dashboard: pseudoLocalizeCatalog(enDashboard),
      discover: pseudoLocalizeCatalog(enDiscover),
      errors: pseudoLocalizeCatalog(enErrors),
      explorer: pseudoLocalizeCatalog(enExplorer),
      knowledge: pseudoLocalizeCatalog(enKnowledge),
      notifications: pseudoLocalizeCatalog(enNotifications),
      onboarding: pseudoLocalizeCatalog(enOnboarding),
      organisations: pseudoLocalizeCatalog(enOrganisations),
      'pulse-ai': pseudoLocalizeCatalog(enPulseAi),
      'quick-drafts': pseudoLocalizeCatalog(enQuickDrafts),
      reporting: pseudoLocalizeCatalog(enReporting),
      settings: pseudoLocalizeCatalog(enSettings),
      'share-viewer': pseudoLocalizeCatalog(enShareViewer),
      tasks: pseudoLocalizeCatalog(enTasks),
      websites: pseudoLocalizeCatalog(enWebsites),
    },
  };
}

/**
 * Catalog for a language that is not bundled eagerly, or `null` when the
 * language needs no load — `en` is already in {@link BASE_RESOURCES}, and the
 * pseudo-locale is generated from it.
 *
 * Resolved catalogs are cached by the module registry, so repeated calls for the
 * same language share one network fetch; the in-flight promise is memoised too,
 * so two callers racing at boot cannot start two.
 */
const catalogLoads = new Map<string, Promise<ResourceLanguage | null>>();

export async function loadLanguageCatalog(language: string): Promise<ResourceLanguage | null> {
  // Match `fr-CA` and friends, not just the bare tag: i18next resolves a
  // regional code against its base language, so the base is what we must load.
  const base = language.split('-')[0];
  if (base !== 'fr') {
    return null;
  }

  const cached = catalogLoads.get(base);
  if (cached !== undefined) {
    return await cached;
  }

  const load = import('./fr-catalog')
    .then((mod): ResourceLanguage => mod.default)
    .catch((error: unknown) => {
      // A failed catalog fetch is non-fatal: `fallbackLng: 'en'` keeps every
      // `t()` resolving. Drop the rejected promise so a later attempt can retry
      // rather than replaying this failure forever.
      catalogLoads.delete(base);
      throw error;
    });

  // Stored before awaiting, so a second caller racing at boot joins this load
  // instead of starting its own.
  catalogLoads.set(base, load);
  return await load;
}

/** Production locales. */
export const SUPPORTED_LNGS: string[] = [...SUPPORTED_LANGUAGES];

/** Dev locales — production set plus the pseudo-locale. */
export const SUPPORTED_LNGS_WITH_PSEUDO: string[] = [...SUPPORTED_LANGUAGES, PSEUDO_LOCALE];
